/**
 * Hyperliquid WebSocket Connection Pool
 *
 * Module-level singleton that multiplexes all HL WebSocket subscriptions
 * over a single connection. Ref-counted: opens on first subscribe,
 * closes when all unsubscribe.
 *
 * Key wins over per-hook connections:
 * - 1 WS connection instead of 5
 * - activeAssetCtx shared between market-ticker and mark-price hooks
 * - Centralized reconnect with exponential backoff
 */

import {
  HL_MAINNET_WS,
  HL_TESTNET_WS,
  HL_WS_MAX_RECONNECT_ATTEMPTS,
  HL_WS_INITIAL_RECONNECT_DELAY,
  HL_WS_MAX_RECONNECT_DELAY,
  HL_WS_PING_INTERVAL,
} from "../client";
import type {
  HLWsSubscription,
  HLWsMessage,
} from "../client";

// ============================================================================
// Types
// ============================================================================

export type HLWsCallback = (msg: HLWsMessage) => void;

interface SubscriptionEntry {
  sub: HLWsSubscription;
  callbacks: Set<HLWsCallback>;
}

// ============================================================================
// Key Helpers
// ============================================================================

/** Stable key for a subscription request. */
function subKey(sub: HLWsSubscription): string {
  if (sub.type === "candle") {
    return `candle:${sub.coin ?? "*"}:${sub.interval ?? "1m"}`;
  }
  return `${sub.type}:${sub.coin ?? "*"}`;
}

/** Extract routing key from an incoming WS message. Returns null for non-data messages. */
function msgToKey(msg: Record<string, unknown>): string | null {
  const channel = msg.channel as string;
  const data = msg.data;

  switch (channel) {
    case "l2Book":
      return `l2Book:${(data as { coin: string }).coin}`;
    case "trades": {
      const trades = data as { coin: string }[];
      return `trades:${trades[0]?.coin ?? "*"}`;
    }
    case "allMids":
      return "allMids:*";
    case "activeAssetCtx":
      return `activeAssetCtx:${(data as { coin: string }).coin}`;
    case "candle": {
      const candle = data as { s: string; i: string };
      return `candle:${candle.s}:${candle.i}`;
    }
    default:
      // subscriptionResponse, error, pong, etc.
      return null;
  }
}

// ============================================================================
// Pool Class
// ============================================================================

class HLWsPool {
  private ws: WebSocket | null = null;
  private wsId = 0; // Tracks current WS instance to ignore stale handlers
  private subscriptions = new Map<string, SubscriptionEntry>();
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly wsUrl: string;

  constructor(testnet: boolean) {
    this.wsUrl = testnet ? HL_TESTNET_WS : HL_MAINNET_WS;
  }

  /**
   * Subscribe to a channel. Returns an unsubscribe function.
   * First subscriber opens the connection; last unsubscribe closes it.
   */
  subscribe(sub: HLWsSubscription, callback: HLWsCallback): () => void {
    const key = subKey(sub);
    let entry = this.subscriptions.get(key);

    if (!entry) {
      entry = { sub, callbacks: new Set() };
      this.subscriptions.set(key, entry);

      // If already connected, send subscribe immediately
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendRequest({ method: "subscribe", subscription: sub });
      }
    }

    entry.callbacks.add(callback);
    this.cancelCloseTimer();
    this.ensureConnected();

    return () => {
      const e = this.subscriptions.get(key);
      if (!e) return;
      e.callbacks.delete(callback);

      if (e.callbacks.size === 0) {
        this.subscriptions.delete(key);

        if (this.ws?.readyState === WebSocket.OPEN) {
          this.sendRequest({ method: "unsubscribe", subscription: sub });
        }

        // Debounce close — give React effects time to re-subscribe
        // (e.g., when adapter identity changes and hooks re-fire)
        if (this.subscriptions.size === 0) {
          this.scheduleClose();
        }
      }
    };
  }

  // ==========================================================================
  // Connection lifecycle
  // ==========================================================================

  private ensureConnected(): void {
    if (typeof WebSocket === "undefined") return; // SSR guard
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    this.connect();
  }

  private connect(): void {
    if (typeof WebSocket === "undefined") return;

    // Increment connection ID — handlers from previous WS instances will be ignored
    const connectionId = ++this.wsId;

    try {
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        // Ignore if this is a stale connection
        if (connectionId !== this.wsId) return;

        this.reconnectAttempts = 0;

        // Re-subscribe all active subscriptions
        for (const entry of this.subscriptions.values()) {
          this.sendRequest({ method: "subscribe", subscription: entry.sub });
        }

        this.startPing();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        // Ignore messages from stale connections
        if (connectionId !== this.wsId) return;
        this.handleMessage(event);
      };

      this.ws.onclose = () => {
        // Ignore close from stale connections
        if (connectionId !== this.wsId) return;

        this.stopPing();
        if (this.subscriptions.size > 0) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = () => {
        // onerror fires before onclose — reconnect handled there
      };
    } catch {
      if (this.subscriptions.size > 0) {
        this.scheduleReconnect();
      }
    }
  }

  /** Debounce close by 150ms so React effect cleanup/re-subscribe cycles
   *  don't tear down the WS while it's still CONNECTING. */
  private scheduleClose(): void {
    this.cancelCloseTimer();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      if (this.subscriptions.size === 0) {
        this.close();
      }
    }, 150);
  }

  private cancelCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private close(): void {
    this.cancelCloseTimer();
    this.clearReconnectTimer();
    this.stopPing();

    if (this.ws) {
      // Bump wsId so any pending handlers from this WS are ignored
      this.wsId++;
      try {
        if (
          this.ws.readyState === WebSocket.OPEN ||
          this.ws.readyState === WebSocket.CONNECTING
        ) {
          this.ws.close();
        }
      } catch {
        // Ignore close errors
      }
      this.ws = null;
    }
  }

  // ==========================================================================
  // Message routing
  // ==========================================================================

  private handleMessage(event: MessageEvent): void {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(event.data as string) as Record<string, unknown>;
    } catch {
      return; // Ignore unparseable messages
    }

    // HL sends { channel: "...", data: { ... } } for subscription updates
    if (typeof parsed.channel !== "string" || parsed.data === undefined) {
      return; // Ignore pong, etc.
    }

    // Log HL-side errors (e.g., "Invalid subscription")
    if (parsed.channel === "error") {
      console.warn("[HL WS] Server error:", parsed.data);
      return;
    }

    // Ignore subscription confirmations
    if (parsed.channel === "subscriptionResponse") {
      return;
    }

    const key = msgToKey(parsed);
    if (!key) return;

    const entry = this.subscriptions.get(key);
    if (!entry) return;

    const msg = parsed as unknown as HLWsMessage;
    for (const cb of entry.callbacks) {
      try {
        cb(msg);
      } catch (err) {
        console.error("[HL WS] Callback error:", err);
      }
    }
  }

  // ==========================================================================
  // Wire helpers
  // ==========================================================================

  private sendRequest(req: {
    method: "subscribe" | "unsubscribe";
    subscription: HLWsSubscription;
  }): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(req));
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ method: "ping" }));
      }
    }, HL_WS_PING_INTERVAL);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  // ==========================================================================
  // Reconnection
  // ==========================================================================

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= HL_WS_MAX_RECONNECT_ATTEMPTS) {
      return;
    }

    this.clearReconnectTimer();

    const delay = Math.min(
      HL_WS_INITIAL_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts),
      HL_WS_MAX_RECONNECT_DELAY,
    );
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      if (this.subscriptions.size > 0) {
        // Use ensureConnected (not connect) to avoid creating duplicates
        this.ensureConnected();
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

// ============================================================================
// Module-level Singletons
// ============================================================================

let mainnetPool: HLWsPool | null = null;
let testnetPool: HLWsPool | null = null;

/** Get (or create) the shared WS pool for mainnet or testnet. */
export function getHLWsPool(testnet = false): HLWsPool {
  if (testnet) {
    if (!testnetPool) testnetPool = new HLWsPool(true);
    return testnetPool;
  }
  if (!mainnetPool) mainnetPool = new HLWsPool(false);
  return mainnetPool;
}
